import React, { useState } from "react";
import LoginScene from "@/components/three/LoginScene";
import IntroScreen from "@/components/IntroScreen";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, X, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useBotGuard } from "@/lib/botguard";
import { useLang } from "@/i18n";
import api, { apiError } from "@/lib/api";
import { APP_NAME, APP_URL } from "@/lib/app";
import { useCamp, logoUrl } from "@/context/SettingsContext";
import { playWelcome } from "@/lib/welcomeSound";

// Achtergrondfoto van het oorspronkelijke aanmeldscherm (stijl "original")
const ORIGINAL_BG = "https://images.unsplash.com/photo-1589024523068-aef390d41e31?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";


export default function Login() {
  const { user, login, verifyTwofa, resendTwofa } = useAuth();
  const { t, lang, changeLang } = useLang();
  const { camp } = useCamp();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => /(^|\.)cap-blanch\.pro$/i.test(window.location.hostname) ? "jos@macas.be" : "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [twofa, setTwofa] = useState(null); // {challenge_id, phone_hint}
  const [code, setCode] = useState("");
  const [fails, setFails] = useState(0);
  const [mode, setMode] = useState("login"); // login | forgot
  const [fPhone, setFPhone] = useState("");
  const [fSent, setFSent] = useState(false);
  const pwRef = React.useRef(null);
  const guard = useBotGuard();
  const [website, setWebsite] = useState(""); // honeypot
  const style = ["sober", "original", "scene3d"].includes(camp.login_style) ? camp.login_style : "sober";
  const single = style !== "scene3d";

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const attempt = () => login(email.trim(), password, { website, ...guard.extras() });
      let res;
      try { res = await attempt(); }
      catch (err) {
        if (err.response?.status !== 400) throw err;
        await guard.refresh(); // uitdaging verlopen → stil vernieuwen en één keer opnieuw
        res = await attempt();
      }
      if (res?.twofa_required) {
        setTwofa(res);
        setCode("");
        toast.success(`${t("twofa_sent")} ${res.phone_hint || ""}`);
        return;
      }
      toast.success(APP_NAME);
      playWelcome(camp.intro_music !== false);
      navigate("/");
    } catch (err) {
      setFails((n) => n + 1);
      toast.error(apiError(err.response?.data?.detail) || err.message);
      guard.refresh(); guard.resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), phone: fPhone.trim() });
      setFSent(true);
      toast.success(t("reset_sent_msg"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyTwofa(twofa.challenge_id, code.trim());
      toast.success(APP_NAME);
      playWelcome(camp.intro_music !== false);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const res = await resendTwofa(twofa.challenge_id);
      setTwofa(res);
      setCode("");
      toast.success(`${t("twofa_sent")} ${res.phone_hint || ""}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      <IntroScreen />
      {/* Achtergrond volgens de gekozen stijl: zakelijk, origineel of de 3D-scène */}
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        {style === "scene3d" && (
          <>
            <LoginScene />
            <div className="absolute inset-0 bg-gradient-to-br from-[#031b24]/75 via-[#03212c]/35 to-[#03212c]/10" />
          </>
        )}
        {style === "original" && (
          <>
            <img src={ORIGINAL_BG} alt="" className="h-full w-full object-cover scale-110 blur-2xl" />
            <div className="absolute inset-0 bg-slate-950/55" />
          </>
        )}
        {style === "sober" && (
          <div className="absolute inset-0 bg-[#0b1623]">
            <div className="absolute inset-0 opacity-[0.07]"
              style={{ backgroundImage: "linear-gradient(to right,#ffffff 1px,transparent 1px),linear-gradient(to bottom,#ffffff 1px,transparent 1px)", backgroundSize: "64px 64px" }} />
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full bg-sky-500/10 blur-[120px]" />
          </div>
        )}
      </div>

      <div className={`relative z-10 min-h-screen ${single ? "flex items-center justify-center px-5 py-10" : "grid lg:grid-cols-[1.05fr_minmax(420px,0.85fr)]"}`}>
        {/* Merkzijde */}
        {!single && (
        <div className="hidden lg:flex flex-col justify-between px-14 py-14 text-white">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-white/10 border border-white/20 grid place-items-center backdrop-blur-sm">
              <ShieldCheck className="h-5 w-5 text-cyan-300" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.42em] text-white/70">{APP_NAME}</span>
          </div>
          <div className="max-w-xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.42em] text-cyan-300/90">{t("tagline")}</p>
            <h2 className="mt-4 font-display font-extrabold text-5xl xl:text-6xl leading-[1.02] tracking-tight">
              {camp.camp_name || APP_NAME}
            </h2>
            <p className="mt-5 text-base text-white/70 leading-relaxed max-w-md">{t("login_sub")}</p>
          </div>
          <div className="flex items-center gap-6 text-xs text-white/50">
            <span className="flex items-center gap-2"><span className="live-dot" />{t("live_updated")}</span>
            <span>{new Date().toLocaleDateString()}</span>
          </div>
        </div>
        )}

        {/* Aanmeldzijde */}
        <div className={single ? "w-full max-w-[420px]" : "flex items-center justify-center px-5 py-10 sm:px-10"}>
        <div className={`relative w-full max-w-[420px] cb-fade-up px-7 sm:px-9 py-9 ${style === "original"
          ? "rounded-[26px] bg-[#0e1420] border border-white/10 shadow-2xl shadow-black/50"
          : "rounded-3xl bg-white/[0.07] border border-white/15 backdrop-blur-2xl shadow-[0_24px_80px_-24px_rgba(0,0,0,0.75)]"}`}>
          <button
            type="button"
            onClick={() => { window.location.href = APP_URL; }}
            className="absolute top-4 right-4 h-9 w-9 rounded-full grid place-items-center text-white/45 hover:text-white hover:bg-white/10 transition-colors duration-150"
            aria-label="Annuleren"
            title="Annuleren"
            data-testid="login-cancel-button"
          >
            <X className="h-5 w-5" />
          </button>
          {single && (
            <div className="mb-5 grid place-items-center">
              {camp.logo_file_id ? (
                <img src={logoUrl(camp, "main")} alt={camp.camp_name || APP_NAME} className="h-12 w-auto max-w-[220px] object-contain" data-testid="login-logo" />
              ) : (
                <span className="h-14 w-14 rounded-2xl bg-white/[0.06] border border-white/10 grid place-items-center">
                  <ShieldCheck className="h-7 w-7 text-sky-400" />
                </span>
              )}
            </div>
          )}
          <p className={`font-display font-extrabold text-xl tracking-tight text-white text-center ${single ? "" : "lg:hidden"}`} data-testid={camp.camp_name ? "login-camp-name" : "login-app-name"}>
            {camp.camp_name || APP_NAME}
          </p>
          <p className="text-[10px] tracking-[0.42em] text-cyan-300/90 font-semibold uppercase text-center" data-testid={camp.camp_name ? "login-app-name-small" : "login-tagline"}>
            {camp.camp_name ? APP_NAME : t("tagline")}
          </p>

          <h1 className="mt-3 font-display font-extrabold text-[2rem] leading-tight tracking-tight text-white text-center">
            {twofa ? t("login_title") : mode === "forgot" ? t("reset_title") : t("login_title")}
          </h1>
          <p className="mt-2 text-sm text-white/55 leading-relaxed text-center">{twofa ? `${t("twofa_sub")} ${twofa.phone_hint || ""}` : mode === "forgot" ? t("reset_request_sub") : t("login_sub")}</p>

          {twofa ? (
            <form onSubmit={submitCode} className="mt-7 space-y-4" data-testid="twofa-form">
              <div className="space-y-1.5">
                <Label htmlFor="code" className="text-white/70 text-xs">{t("twofa_code")}</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoFocus
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="h-12 rounded-xl bg-white/[0.06] border-white/15 text-white text-center tracking-[0.5em] text-lg placeholder:text-white/25 focus-visible:ring-cyan-400"
                  data-testid="twofa-code-input"
                />
              </div>
              <Button type="submit" disabled={loading || code.length !== 6}
                className={`w-full h-12 rounded-xl text-base font-semibold transition-all duration-150 group ${single ? "bg-sky-600 hover:bg-sky-500 text-white shadow-lg shadow-sky-900/30" : "bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-[#032029] shadow-lg shadow-cyan-500/20"}`} data-testid="twofa-submit-button">
                {loading ? t("logging_in") : t("twofa_confirm")}
                {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={resend} className="text-cyan-300 hover:text-cyan-200" data-testid="twofa-resend-button">{t("twofa_resend")}</button>
                <button type="button" onClick={() => { setTwofa(null); setPassword(""); }} className="text-white/50 hover:text-white/80" data-testid="twofa-back-button">{t("cancel")}</button>
              </div>
            </form>
          ) : mode === "forgot" ? (
          <form onSubmit={submitForgot} className="mt-7 space-y-4" data-testid="forgot-form">
            {fSent ? (
              <div className="text-center space-y-5">
                <p className="text-sm text-white/70" data-testid="forgot-sent-msg">{t("reset_sent_msg")}</p>
                <button type="button" onClick={() => { setMode("login"); setFSent(false); }} className="text-cyan-300 hover:text-cyan-200 text-sm" data-testid="forgot-back-button">{t("back_to_login")}</button>
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="femail" className="text-white/70 text-xs">{t("reset_email_label")}</Label>
                  <Input id="femail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="naam@camping.be"
                    className="h-12 rounded-xl bg-white/[0.06] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-cyan-400"
                    data-testid="forgot-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fphone" className="text-white/70 text-xs">{t("reset_phone")}</Label>
                  <Input id="fphone" type="tel" value={fPhone} onChange={(e) => setFPhone(e.target.value)}
                    placeholder="+32 ..."
                    className="h-12 rounded-xl bg-white/[0.06] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-cyan-400"
                    data-testid="forgot-phone-input" />
                </div>
                <Button type="submit" disabled={loading}
                  className={`w-full h-12 rounded-xl text-base font-semibold transition-all duration-150 group ${single ? "bg-sky-600 hover:bg-sky-500 text-white shadow-lg shadow-sky-900/30" : "bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-[#032029] shadow-lg shadow-cyan-500/20"}`}
                  data-testid="forgot-submit-button">
                  {loading ? t("logging_in") : t("reset_request_btn")}
                </Button>
                <button type="button" onClick={() => setMode("login")} className="w-full text-center text-sm text-white/50 hover:text-white/80" data-testid="forgot-cancel-button">{t("back_to_login")}</button>
              </>
            )}
          </form>
          ) : (
          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
            <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }} data-testid="login-honeypot" />
            <div ref={guard.widgetRef} data-testid="login-turnstile" />
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-white/70 text-xs">{t("login_identifier")}</Label>
              <Input
                id="email"
                type="text"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => {
                  const v = e.target.value;
                  setEmail(v);
                  if (/^\d{13}$/.test(v.trim())) pwRef.current?.focus();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !password) { e.preventDefault(); pwRef.current?.focus(); }
                }}
                placeholder={t("login_identifier_ph")}
                className="h-12 rounded-xl bg-white/[0.06] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-cyan-400 transition-colors duration-150"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-white/70 text-xs">{t("password")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  ref={pwRef}
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 rounded-xl bg-white/[0.06] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-cyan-400 transition-colors duration-150 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-sky-500 transition-colors duration-150"
                  aria-label={showPassword ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                  title={showPassword ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                  tabIndex={-1}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className={`w-full h-12 rounded-xl text-base font-semibold transition-all duration-150 group ${single ? "bg-sky-600 hover:bg-sky-500 text-white shadow-lg shadow-sky-900/30" : "bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-[#032029] shadow-lg shadow-cyan-500/20"}`}
              data-testid="login-submit-button"
            >
              {loading ? t("logging_in") : t("login")}
              {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
            </Button>
            {fails >= 1 && (
              <button type="button" onClick={() => setMode("forgot")} className="w-full text-center text-sm text-cyan-300 hover:text-cyan-200 transition-colors" data-testid="forgot-password-link">
                {t("forgot_password")}
              </button>
            )}
          </form>
          )}

          <div className="mt-7 flex items-center justify-center gap-2 text-sm">
            {["nl", "es", "en"].map((l) => (
              <button
                key={l}
                onClick={() => changeLang(l)}
                className={`px-3 py-1 rounded-full border transition-colors ${
                  lang === l
                    ? "bg-cyan-400/15 text-cyan-200 border-cyan-400/40"
                    : "bg-transparent border-white/10 text-white/50 hover:text-white/80"
                }`}
                data-testid={`login-lang-${l}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
